/**
 * @file PricingAndStockSection.tsx
 *
 * Pricing and stock fields, mirroring WSB-v2: regular/sale price and weight carry
 * the shop currency / "kg" as a right-side addon, SKU has an explanatory tooltip,
 * and the stock controls are a bold "Stock" heading, two toggles ("only allow one
 * per order", "unlimited stock"), and a full-width number stepper that shows an
 * infinity glyph (disabled) while unlimited stock is on. All values are controlled
 * by the parent; price validation errors come from the form-state hook.
 */

import { useId } from 'react';
import { GeneralField, Icon, Toggle, Tooltip } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../../i18n/messages';
import { useMediaQuery } from '../../../hooks/use-media-query';
import type { ProductFormErrors, ProductFormFields } from '../product-form-types';
import styles from './PricingAndStockSection.module.css';

interface PricingAndStockSectionProps {
  fields: ProductFormFields;
  errors: ProductFormErrors;
  currencySymbol: string;
  onChange: (update: Partial<ProductFormFields>) => void;
}

/**
 * Renders the pricing and stock controls.
 *
 * @param props - The relevant fields, price errors, currency, and change handler.
 *
 * @returns The pricing-and-stock section.
 */
export function PricingAndStockSection({
  fields,
  errors,
  currencySymbol,
  onChange,
}: PricingAndStockSectionProps) {
  const intl = useIntl();
  const isMobile = useMediaQuery('Mobile');
  const isDigital = fields.productType === 'digital';
  const salePriceError = errors.salePriceFormatError ?? errors.salePriceCheckError;

  const regularPriceId = useId();
  const salePriceId = useId();
  const skuId = useId();
  const weightId = useId();

  const currencySuffix = <span className="gv-px-md gv-text-sm">{currencySymbol}</span>;
  const weightSuffix = (
    <span className="gv-px-md gv-text-sm">
      <FormattedMessage {...adminMessages.formWeightUnit} />
    </span>
  );

  const setStock = (next: number): void => {
    if (fields.unlimitedStock) return;
    onChange({ stockQuantity: Math.max(0, next) });
  };

  return (
    <>
      <div className="gv-flex gv-flex-row gv-gap-md gv-w-full gv-max-tab-flex-col">
        <div
          className="gv-form-option gv-w-full"
          {...(errors.regularPriceError ? { 'data-field-error': true } : {})}
        >
          <label htmlFor={regularPriceId} className="gv-label">
            <span>
              <FormattedMessage {...adminMessages.formRegularPrice} />
            </span>
          </label>
          <GeneralField
            id={regularPriceId}
            value={fields.regularPrice}
            inputMode="decimal"
            autoComplete="off"
            addon={currencySuffix}
            error={errors.regularPriceError}
            data-testid="product-form-regular-price"
            aria-label={intl.formatMessage(adminMessages.formRegularPrice)}
            onChange={(event) => onChange({ regularPrice: event.target.value })}
          />
        </div>

        <div
          className="gv-form-option gv-w-full"
          {...(salePriceError ? { 'data-field-error': true } : {})}
        >
          <label htmlFor={salePriceId} className="gv-label">
            <span>
              <FormattedMessage {...adminMessages.formSalePrice} />
            </span>
            <span className="gv-label-optional">
              <FormattedMessage {...adminMessages.formOptional} />
            </span>
          </label>
          <GeneralField
            id={salePriceId}
            value={fields.salePrice}
            inputMode="decimal"
            autoComplete="off"
            addon={currencySuffix}
            error={salePriceError}
            data-testid="product-form-sale-price"
            aria-label={intl.formatMessage(adminMessages.formSalePrice)}
            onChange={(event) => onChange({ salePrice: event.target.value })}
          />
        </div>
      </div>

      <div className="gv-flex gv-flex-row gv-gap-md gv-w-full gv-max-tab-flex-col">
        <div className="gv-form-option gv-w-full">
          <label htmlFor={skuId} className="gv-label gv-flex gv-flex-row gv-items-center">
            <span className="gv-flex gv-items-center gv-gap-xs">
              <FormattedMessage {...adminMessages.formSku} />
              <Tooltip
                condensed
                position={isMobile ? 'top-left' : 'top-center'}
                trigger={<Icon name="info" size="small" />}
              >
                <div
                  className={styles.tooltipContainer}
                  aria-label={intl.formatMessage(adminMessages.formSkuTooltipAria)}
                >
                  <span>{intl.formatMessage(adminMessages.formSkuTooltip)}</span>
                </div>
              </Tooltip>
            </span>
            <span className="gv-label-optional">
              <FormattedMessage {...adminMessages.formOptional} />
            </span>
          </label>
          <GeneralField
            id={skuId}
            value={fields.sku}
            autoComplete="off"
            spellCheck={false}
            data-testid="product-form-sku"
            aria-label={intl.formatMessage(adminMessages.formSku)}
            onChange={(event) => onChange({ sku: event.target.value })}
          />
        </div>

        {!isDigital && (
          <div className="gv-form-option gv-w-full">
            <label htmlFor={weightId} className="gv-label">
              <span>
                <FormattedMessage {...adminMessages.formWeight} />
              </span>
              <span className="gv-label-optional">
                <FormattedMessage {...adminMessages.formOptional} />
              </span>
            </label>
            <GeneralField
              id={weightId}
              value={fields.weight}
              inputMode="decimal"
              autoComplete="off"
              placeholder="0,00"
              addon={weightSuffix}
              data-testid="product-form-weight"
              aria-label={intl.formatMessage(adminMessages.formWeight)}
              onChange={(event) => onChange({ weight: event.target.value })}
            />
          </div>
        )}
      </div>

      <div className="gv-flex gv-flex-col gv-gap-md gv-w-full">
        <div className="gv-flex gv-flex-col gv-gap-sm gv-w-full">
          <span className="gv-text-sm gv-text-bold">
            <FormattedMessage {...adminMessages.formStock} />
          </span>

          <div
            className="gv-flex gv-gap-sm gv-items-start"
            data-testid="product-form-sold-individually"
          >
            <div className={`gv-pt-xs ${styles.noShrink}`}>
              <Toggle
                condensed
                checked={fields.soldIndividually}
                onChange={(checked) => onChange({ soldIndividually: checked })}
              />
            </div>
            <span className="gv-text-sm gv-pt-xs">
              <FormattedMessage {...adminMessages.formSoldIndividually} />
            </span>
          </div>
        </div>

        <div
          className="gv-flex gv-gap-sm gv-items-center"
          data-testid="product-form-unlimited-stock"
        >
          <div className="gv-pt-xs">
            <Toggle
              condensed
              checked={fields.unlimitedStock}
              onChange={(checked) => onChange({ unlimitedStock: checked })}
            />
          </div>
          <span className="gv-text-sm">
            <FormattedMessage {...adminMessages.formUnlimitedStock} />
          </span>
        </div>

        <div className="gv-flex gv-gap-md">
          <div
            className={`gv-flex gv-gap-md gv-items-center gv-flex-1 gv-p-sm gv-input gv-input-number ${
              fields.unlimitedStock ? 'gv-disabled' : ''
            }`}
          >
            <button
              type="button"
              className={styles.stepperButton}
              aria-label={intl.formatMessage(adminMessages.formDecreaseStock)}
              onClick={() => setStock(fields.stockQuantity - 1)}
              disabled={fields.unlimitedStock || fields.stockQuantity <= 0}
            >
              <Icon name="remove" size="small" />
            </button>
            {fields.unlimitedStock ? (
              <span className="gv-text-sm gv-w-full gv-text-center">∞</span>
            ) : (
              <input
                type="number"
                min={0}
                className="gv-text-sm gv-flex gv-flex-1 gv-items-center"
                data-testid="product-form-stock"
                aria-label={intl.formatMessage(adminMessages.formStock)}
                value={fields.stockQuantity}
                onChange={(event) =>
                  setStock(Number.parseInt(event.target.value, 10) || 0)
                }
              />
            )}
            <button
              type="button"
              className={styles.stepperButton}
              aria-label={intl.formatMessage(adminMessages.formIncreaseStock)}
              onClick={() => setStock(fields.stockQuantity + 1)}
              disabled={fields.unlimitedStock}
            >
              <Icon name="add" size="small" />
            </button>
          </div>
          <div className="gv-flex-1 gv-p-md gv-max-mob-hidden" />
        </div>
      </div>
    </>
  );
}
